import { useState, useEffect } from 'react';
import supportApiService from '../../services/supportApiService';
import { toast } from 'react-toastify';

const SupportRefundInfo = () => {
  const [loading, setLoading] = useState(true);
  const [refunds, setRefunds] = useState([]);
  const [filteredRefunds, setFilteredRefunds] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [paymentModeFilter, setPaymentModeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [totalRefunds, setTotalRefunds] = useState(0);

  useEffect(() => {
    loadRefundData();
  }, []);

  useEffect(() => {
    filterRefunds();
  }, [refunds, searchTerm, paymentModeFilter, statusFilter]);

  const loadRefundData = async () => {
    try {
      setLoading(true);
      console.log('🏨 [SUPPORT-REFUND-INFO] Loading refund data...');
      
      const response = await supportApiService.getRefundInfo();
      
      if (response.success) {
        console.log('✅ Refund data loaded:', response);
        setRefunds(response.refund || []);
        setTotalRefunds(response.totalRefunds || 0);
        toast.success('Refund data loaded successfully');
      } else {
        console.error('❌ Failed to load refund data:', response.message);
        toast.error(response.message || 'Failed to load refund data');
      }
    } catch (error) {
      console.error('❌ Error loading refund data:', error);
      toast.error('Failed to load refund data');
    } finally {
      setLoading(false);
    }
  };

  const filterRefunds = () => {
    let filtered = refunds;

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(refund =>
        refund.agentId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        refund.agentName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        refund.agentEmail?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        refund.transactionId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        refund.refundId?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply payment mode filter
    if (paymentModeFilter !== 'all') {
      filtered = filtered.filter(refund => 
        refund.modeOfPayment?.toLowerCase() === paymentModeFilter.toLowerCase()
      );
    }

    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(refund => 
        refund.refundStatus?.toLowerCase() === statusFilter.toLowerCase()
      );
    }

    setFilteredRefunds(filtered);
  };

  const getPaymentModeBadge = (mode) => {
    const modeLower = mode?.toLowerCase() || '';
    switch (modeLower) {
      case 'credit card':
      case 'creditcard':
        return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">Credit Card</span>;
      case 'debit card':
      case 'debitcard':
        return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-purple-100 text-purple-800">Debit Card</span>;
      case 'upi':
        return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-orange-100 text-orange-800">UPI</span>;
      case 'net banking':
      case 'netbanking':
        return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-indigo-100 text-indigo-800">Net Banking</span>;
      case 'wallet':
        return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">Wallet</span>;
      default:
        return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800">{mode || 'Unknown'}</span>;
    }
  };

  const getStatusBadge = (status) => {
    const statusLower = status?.toLowerCase() || '';
    switch (statusLower) {
      case 'completed':
      case 'success':
      case 'processed':
        return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">Completed</span>;
      case 'pending':
      case 'processing':
        return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">Pending</span>;
      case 'failed':
      case 'rejected':
        return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">Failed</span>;
      case 'cancelled':
        return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800">Cancelled</span>;
      default:
        return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800">{status || 'Unknown'}</span>;
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return 'N/A';
    const numAmount = parseFloat(amount.toString().replace(/[^\d.-]/g, ''));
    if (isNaN(numAmount)) return amount;
    
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(numAmount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-IN', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      return dateString;
    }
  };

  // Get unique payment modes for filter
  const uniquePaymentModes = [...new Set(refunds.map(r => r.modeOfPayment).filter(Boolean))];
  const uniqueStatuses = [...new Set(refunds.map(r => r.refundStatus).filter(Boolean))];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-6 max-w-7xl">
        {/* Page Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Payment Refund Information</h1>
          <p className="text-gray-600">Manage and track refund requests and processing</p>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 rounded-lg">
                <i className="fas fa-undo text-green-600"></i>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-600">Total Refunds</p>
                <p className="text-lg font-semibold text-gray-900">{totalRefunds}</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 rounded-lg">
                <i className="fas fa-check-circle text-green-600"></i>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-600">Completed</p>
                <p className="text-lg font-semibold text-gray-900">
                  {refunds.filter(r => ['completed', 'success', 'processed'].includes(r.refundStatus?.toLowerCase())).length}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <i className="fas fa-clock text-yellow-600"></i>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-600">Pending</p>
                <p className="text-lg font-semibold text-gray-900">
                  {refunds.filter(r => ['pending', 'processing'].includes(r.refundStatus?.toLowerCase())).length}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 rounded-lg">
                <i className="fas fa-calculator text-blue-600"></i>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-600">Total Amount</p>
                <p className="text-lg font-semibold text-gray-900">
                  {formatCurrency(
                    refunds.reduce((sum, r) => {
                      const amount = parseFloat(r.refundAmount?.toString().replace(/[^\d.-]/g, '') || 0);
                      return sum + (isNaN(amount) ? 0 : amount);
                    }, 0)
                  )}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Search and Filter Controls */}
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <i className="fas fa-search text-gray-400"></i>
                </div>
                <input
                  type="text"
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-green-500 focus:border-green-500"
                  placeholder="Search by Agent ID, Name, Email, Transaction ID, or Refund ID..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
            <div className="md:w-48">
              <select
                className="block w-full px-3 py-2 border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-green-500 focus:border-green-500"
                value={paymentModeFilter}
                onChange={(e) => setPaymentModeFilter(e.target.value)}
              >
                <option value="all">All Payment Modes</option>
                {uniquePaymentModes.map(mode => (
                  <option key={mode} value={mode}>{mode}</option>
                ))}
              </select>
            </div>
            <div className="md:w-48">
              <select
                className="block w-full px-3 py-2 border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-green-500 focus:border-green-500"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="all">All Status</option>
                {uniqueStatuses.map(status => (
                  <option key={status} value={status}>{status}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Refund Information Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Payment Refund Details</h3>
            <p className="text-sm text-gray-600">
              Showing {filteredRefunds.length} of {refunds.length} refunds
            </p>
          </div>

          {filteredRefunds.length === 0 ? (
            <div className="p-8 text-center">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="fas fa-undo text-gray-400 text-2xl"></i>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Refunds Found</h3>
              <p className="text-gray-600">
                {searchTerm || paymentModeFilter !== 'all' || statusFilter !== 'all'
                  ? 'No refunds match your current filters.' 
                  : 'No refund records have been found yet.'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Agent ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Payment Mode
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Refund Amount
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Refund Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Transaction ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredRefunds.map((refund, index) => (
                    <tr key={refund.id || index} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-green-600">
                          {refund.agentId || 'N/A'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{refund.agentName || 'N/A'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{refund.agentEmail || 'N/A'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getPaymentModeBadge(refund.modeOfPayment)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-semibold text-gray-900">
                          {formatCurrency(refund.refundAmount)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{formatDate(refund.refundDate)}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 font-mono">{refund.transactionId || 'N/A'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(refund.refundStatus)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SupportRefundInfo;